import React, { useEffect, useState } from 'react';
import { getLogoForMethod } from '../../utils/paymentLogos';
import getApiBaseUrl from '../../utils/api';
import { getCachedUsdToIdrRate, ensureUsdToIdrRate } from '../../utils/currency';

const XenditPaymentMethods = ({ onPaymentMethodSelect, selectedMethod, totalUsd = 0 }) => {
  const [usdToIdrRate, setUsdToIdrRate] = useState(getCachedUsdToIdrRate());
  const totalIdr = Math.round((Number(totalUsd) || 0) * usdToIdrRate);
  const isOtcOverLimit = (method) => method.type === 'OVER_THE_COUNTER' && (method.channelCode === 'INDOMARET' || method.channelCode === 'ALFAMART') && totalIdr > 5000000;
  const [paymentMethods, setPaymentMethods] = useState([
    // Cards - In Progress  
    { 
      id: 'CARD', 
      type: 'CARD', 
      name: 'Credit & Debit Cards', 
      category: 'cards',
      description: 'Visa, Mastercard, JCB, Amex',
      status: 'progress',
      icon: '💳'
    },

    // QR Codes - In Progress
    { 
      id: 'QRIS', 
      type: 'QR_CODE', 
      channelCode: 'QRIS', 
      name: 'QRIS', 
      category: 'qr',
      description: 'Scan QR code to pay',
      status: 'progress',
      icon: '📱'
    },

    // E-Wallets - Activated
    { 
      id: 'ASTRAPAY', 
      type: 'EWALLET', 
      channelCode: 'ASTRAPAY', 
      name: 'AstraPay', 
      category: 'ewallet',
      description: 'Digital wallet payment',
      status: 'active',
      icon: '💰'
    },

    // PayLater - Activated
    { 
      id: 'AKULAKU', 
      type: 'PAYLATER', 
      channelCode: 'AKULAKU', 
      name: 'Akulaku', 
      category: 'paylater',
      description: 'Buy now, pay later',
      status: 'active',
      icon: '⏰'
    },

    // Virtual Accounts - Activated
    { 
      id: 'BJB', 
      type: 'VIRTUAL_ACCOUNT', 
      channelCode: 'BJB', 
      name: 'BJB Virtual Account', 
      category: 'va',
      description: 'Bank transfer via BJB',
      status: 'active',
      icon: '🏦'
    },
    { 
      id: 'BNI', 
      type: 'VIRTUAL_ACCOUNT', 
      channelCode: 'BNI', 
      name: 'BNI Virtual Account', 
      category: 'va',
      description: 'Bank transfer via BNI',
      status: 'active',
      icon: '🏦'
    },
    { 
      id: 'BRI', 
      type: 'VIRTUAL_ACCOUNT', 
      channelCode: 'BRI', 
      name: 'BRI Virtual Account', 
      category: 'va',
      description: 'Bank transfer via BRI',
      status: 'active',
      icon: '🏦'
    },
    { 
      id: 'BSI', 
      type: 'VIRTUAL_ACCOUNT', 
      channelCode: 'BSI', 
      name: 'BSI Virtual Account', 
      category: 'va',
      description: 'Bank transfer via BSI',
      status: 'active',
      icon: '🏦'
    },
    { 
      id: 'BSS', 
      type: 'VIRTUAL_ACCOUNT', 
      channelCode: 'BSS', 
      name: 'BSS Virtual Account', 
      category: 'va',
      description: 'Bank transfer via BSS',
      status: 'active',
      icon: '🏦'
    },
    { 
      id: 'CIMB', 
      type: 'VIRTUAL_ACCOUNT', 
      channelCode: 'CIMB', 
      name: 'CIMB Niaga Virtual Account', 
      category: 'va',
      description: 'Bank transfer via CIMB',
      status: 'active',
      icon: '🏦'
    },
    { 
      id: 'MANDIRI', 
      type: 'VIRTUAL_ACCOUNT', 
      channelCode: 'MANDIRI', 
      name: 'Mandiri Virtual Account', 
      category: 'va',
      description: 'Bank transfer via Mandiri',
      status: 'active',
      icon: '🏦'
    },
    { 
      id: 'PERMATA', 
      type: 'VIRTUAL_ACCOUNT', 
      channelCode: 'PERMATA', 
      name: 'Permata Virtual Account', 
      category: 'va',
      description: 'Bank transfer via Permata',
      status: 'active',
      icon: '🏦'
    },

    // Over-The-Counter - Activated
    { 
      id: 'INDOMARET', 
      type: 'OVER_THE_COUNTER', 
      channelCode: 'INDOMARET', 
      name: 'Indomaret', 
      category: 'otc',
      description: 'Pay at Indomaret stores (max Rp 5.000.000)',
      status: 'active',
      icon: '🏪'
    }
  ]);

  useEffect(() => {
    let isMounted = true;

    ensureUsdToIdrRate().then(rate => {
      if (isMounted) {
        setUsdToIdrRate(rate);
      }
    });

    const fetchMethods = async () => {
      try {
        const base = getApiBaseUrl();
        const res = await fetch(`${base}/xendit/methods`);
        if (!res.ok) return; // fallback to static
        const data = await res.json();
        if (data?.success && Array.isArray(data.methods) && data.methods.length > 0) {
          setPaymentMethods(data.methods);
        }
      } catch (_) {/* keep static fallback */}
    };
    fetchMethods();
    return () => {
      isMounted = false;
    };
  }, []);

  const handleMethodSelect = (method) => {
    onPaymentMethodSelect({
      id: method.id,
      type: method.type,
      channelCode: method.channelCode,
      name: method.name,
      category: method.category
    });
  };

  // Group methods by category for better organization
  const groupedMethods = [
    { category: 'cards', title: 'Credit & Debit Cards', methods: paymentMethods.filter(m => m.category === 'cards') },
    { category: 'qr', title: 'QR Codes', methods: paymentMethods.filter(m => m.category === 'qr') },
    { category: 'ewallet', title: 'E-Wallets', methods: paymentMethods.filter(m => m.category === 'ewallet') },
    { category: 'paylater', title: 'Pay Later', methods: paymentMethods.filter(m => m.category === 'paylater') },
    { category: 'va', title: 'Bank Transfer', methods: paymentMethods.filter(m => m.category === 'va') },
    { category: 'otc', title: 'Store Payment', methods: paymentMethods.filter(m => m.category === 'otc') }
  ].filter(group => group.methods.length > 0);

  return (
    <div className="space-y-4">
      <h3 className="text-lg font-semibold text-light">Select Payment Method</h3>

      {totalIdr > 5000000 && (
        <div className="bg-yellow-900/20 border border-yellow-800 rounded-md p-3 text-sm text-yellow-300">
          Large amount detected. For payments above Rp 5.000.000, we recommend Bank Transfer (Virtual Account).
        </div>
      )}

      {/* All Payment Methods in Vertical List */}
      <div className="space-y-6">
        {groupedMethods.map(group => (
          <div key={group.category}>
            <h4 className="text-sm font-medium text-gray-400 mb-3 flex items-center">
              <span className="mr-2">{group.methods[0]?.icon}</span>
              {group.title}
            </h4>
            
            <div className="space-y-2">
              {group.methods.map((method) => (
                <div
                  key={method.id}
                  className={`relative rounded-lg border-2 transition-all duration-200 ${
                    method.status === 'progress' || isOtcOverLimit(method)
                      ? 'border-gray-800 bg-secondary opacity-70 cursor-not-allowed'
                      : selectedMethod?.id === method.id
                        ? 'border-primary bg-primary/10 shadow-md ring-2 ring-primary/20 cursor-pointer'
                        : 'border-gray-700 hover:border-gray-600 hover:shadow-sm bg-secondary cursor-pointer'
                  }`}
                  onClick={() => method.status !== 'progress' && !isOtcOverLimit(method) && handleMethodSelect(method)}
                >
                  <div className="p-4">
                    <div className="flex items-center justify-between">
                      <div className="flex items-center space-x-3">
                        <div className={`w-5 h-5 rounded-full border-2 flex items-center justify-center ${
                          method.status === 'progress' || isOtcOverLimit(method)
                            ? 'border-gray-600'
                            : selectedMethod?.id === method.id
                              ? 'border-primary bg-primary'
                              : 'border-gray-400'
                        }`}>
                          {selectedMethod?.id === method.id && (
                            <div className="w-2 h-2 rounded-full bg-dark"></div>
                          )}
                        </div>
                        <div className="flex items-center space-x-3">
                          <img
                            src={getLogoForMethod(method)}
                            alt={method.name}
                            className="w-8 h-8 rounded-sm object-contain bg-white/5"
                            onError={(e) => { e.currentTarget.style.display = 'none'; }}
                          />
                          <h4 className="font-medium text-light flex items-center space-x-2">
                            <span>{method.name}</span>
                          {method.status === 'progress' && (
                            <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-yellow-900/30 text-yellow-400">
                              In Progress
                            </span>
                          )}
                          {group.category === 'va' && totalIdr > 5000000 && (
                            <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-green-900/30 text-green-400">
                              Recommended
                            </span>
                          )}
                          {isOtcOverLimit(method) && (
                            <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-red-900/30 text-red-400">
                              Over Limit
                            </span>
                          )}
                          </h4>
                          <p className="text-sm text-gray-400">{method.description}</p>
                        </div>
                      </div>
                      
                      {method.status !== 'progress' && !isOtcOverLimit(method) && selectedMethod?.id === method.id && (
                        <div className="flex items-center justify-center w-6 h-6 rounded-full bg-primary">
                          <svg className="w-4 h-4 text-dark" fill="currentColor" viewBox="0 0 20 20">
                            <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                          </svg>
                        </div>
                      )}
                    </div>
                  </div>

                  {/* Status indicator for "In Progress" methods */}
                  {method.status === 'progress' && (
                    <div className="absolute top-2 right-2">
                      <div className="w-2 h-2 bg-yellow-400 rounded-full animate-pulse"></div>
                    </div>
                  )}
                  {isOtcOverLimit(method) && (
                    <div className="absolute top-2 right-2">
                      <div className="px-2 py-0.5 rounded text-xs font-medium bg-red-900/30 text-red-400 border border-red-800/40">Max Rp 5M</div>
                    </div>
                  )}
                </div>
              ))}
            </div>
          </div>
        ))}
      </div>

      {/* Information notice */}
      <div className="bg-blue-900/20 border border-blue-800 rounded-lg p-4">
        <div className="flex">
          <div className="flex-shrink-0">
            <svg className="h-5 w-5 text-blue-400" viewBox="0 0 20 20" fill="currentColor">
              <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z" clipRule="evenodd" />
            </svg>
          </div>
          <div className="ml-3">
            <h3 className="text-sm font-medium text-blue-400">Payment Information</h3>
            <div className="mt-2 text-sm text-blue-300">
              <p>Methods marked as "In Progress" are being activated and will be available soon. All other methods are ready for use.</p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default XenditPaymentMethods;
